/**
 * Pages « Pour qui » (/pour/<slug>) : une page par métier, qui répond à la requête « logiciel de Gantt pour … ».
 *
 * Même format de contenu que le blog (blocs `h2`, `p`, `ul`, `table`… et mini-syntaxe `**gras**` /
 * `[libellé](/blog/slug)`, voir src/data/blogPosts.js). Rendues par src/pages/UseCasePage.jsx et pré-rendues par
 * scripts/seo-build.mjs. Chargé tel quel par Node : pas d'alias `@/`, pas de JSX.
 *
 * Règle éditoriale (docs/SEO_GEO.md) : ne décrire que ce que le code confirme, aucune statistique inventée.
 *
 * Champs :
 *  - slug, label   : segment d'URL et libellé court (liens, fil d'Ariane)
 *  - title         : <title> et og:title (≈ 60 caractères, « — FlowGantt » compris)
 *  - h1            : titre visible
 *  - description   : méta-description (120-160 caractères)
 *  - updated       : date de la dernière révision de fond (sitemap, dateModified)
 *  - keywords      : requêtes visées
 *  - summary       : la réponse en trois phrases, en tête de page (ce que citent les moteurs de réponse)
 *  - body, faq     : contenu et FAQ (FAQ visible et FAQPage JSON-LD)
 *  - related       : guides du blog liés (slugs)
 */

const PRICING_ANSWER =
  "Le plan gratuit permet 1 projet actif de 30 tâches, sans carte bancaire et sans limite de durée, avec 14 jours de Pro offerts à l'inscription. Pro coûte 9 € par mois : projets et tâches illimités, logo personnalisé et génération de plan par IA, réservée aux abonnés ; Équipe coûte 29 € par mois avec 5 sièges inclus, puis 6 € par siège supplémentaire. Détail sur la [page Tarifs](/pricing).";

const useCases = [
  {
    slug: "agences",
    label: "Agences",
    title: "Logiciel de diagramme de Gantt pour agences — FlowGantt",
    h1: "Le diagramme de Gantt des agences qui livrent leurs clients à l'heure",
    description:
      "Planning Gantt en ligne pour agences web, design et communication : projets clients, jalons de validation, lien de partage client et exports à votre logo.",
    updated: "2026-10-30",
    keywords: [
      "logiciel gantt agence",
      "planning agence web",
      "gestion de projet agence",
      "planning client agence",
      "diagramme de gantt agence de communication",
    ],
    summary:
      "FlowGantt est un logiciel de diagramme de Gantt en ligne adapté aux agences web, de design et de communication : chaque projet client a son planning, avec des jalons pour les validations, des dépendances qui recalculent les dates quand un retour client arrive en retard, et un lien de partage en lecture seule pour le client. Sur les offres Pro et Équipe, les exports PDF et les pages partagées portent le logo de l'agence. L'offre Équipe ajoute les rôles Manager et Membre pour répartir le travail.",
    body: [
      { h2: "Ce qui rend le planning d'une agence difficile" },
      {
        p: "Dans une agence, le calendrier ne dépend pas seulement de l'équipe : il dépend du client. Une maquette validée avec une semaine de retard décale l'intégration, la recette et la mise en ligne. Dans un tableur, il faut alors reprendre chaque date à la main, sur chaque projet en cours.",
      },
      {
        ul: [
          "**Plusieurs projets en parallèle** qui partagent les mêmes personnes.",
          "**Des validations client** qui bloquent l'étape suivante et dont la date n'est jamais garantie.",
          "**Un client qui veut voir l'avancement** sans avoir accès à vos outils internes.",
          "**Un planning qui fait partie de la proposition commerciale** et doit donc être présentable.",
        ],
      },
      { h2: "Comment FlowGantt répond à ces besoins" },
      {
        table: {
          head: ["Besoin de l'agence", "Dans FlowGantt"],
          rows: [
            ["Faire glisser tout le planning quand une validation client arrive en retard", "Dépendances FS, SS, FF et SF avec décalages : déplacer une tâche décale les tâches liées en cascade"],
            ["Matérialiser les validations et les livraisons", "Jalons (tâches de durée nulle) affichés comme des losanges sur le Gantt"],
            ["Montrer l'avancement au client", "Lien de partage en lecture seule, avec mot de passe et date d'expiration en option"],
            ["Un document à son image", "Exports PDF, PNG et XLSX ; logo de l'agence sur les exports et les pages partagées (Pro et Équipe)"],
            ["Répartir le travail dans l'équipe", "Offre Équipe : rôles Manager et Membre, mode « progression seule » pour les membres qui ne doivent que mettre à jour leur avancement"],
            ["Ranger les projets clients", "Dossiers de projets et favoris sur le tableau de bord"],
            ["Ne pas repartir de zéro", "Modèles intégrés (dont une refonte de site web) et enregistrement de n'importe quel projet comme modèle"],
          ],
        },
      },
      { h2: "Un exemple : la refonte d'un site vitrine" },
      {
        p: "Un planning type d'agence enchaîne cadrage, maquettes, validation client, intégration, recette et mise en ligne. Les étapes clés deviennent des jalons, et chaque phase est reliée à la précédente par une dépendance Fin → Début (FS).",
      },
      {
        ol: [
          "**Cadrage** (1 semaine), puis le jalon « Cahier des charges validé ».",
          "**Maquettes** (2 semaines), puis le jalon « Maquettes validées par le client ».",
          "**Intégration** (3 semaines), qui ne démarre qu'après la validation des maquettes.",
          "**Recette** (1 semaine), puis le jalon « Mise en ligne ».",
        ],
      },
      {
        p: "Si le client valide les maquettes trois jours plus tard que prévu, il suffit de déplacer le jalon : l'intégration, la recette et la mise en ligne se décalent d'autant. Le lien partagé montre au client le nouveau calendrier, sans nouvel envoi de fichier. Pour aller plus loin sur les types de liens, lisez [les dépendances FS, SS, FF et SF expliquées](/blog/dependances-fs-ss-ff-sf-expliquees) et [comment utiliser les jalons](/blog/jalons-milestones-comment-les-utiliser).",
      },
      { h2: "Ce que FlowGantt ne fait pas" },
      {
        ul: [
          "Pas de suivi budgétaire ni de facturation au temps passé : FlowGantt planifie, il ne chiffre pas.",
          "Pas de co-édition en temps réel d'un même planning par plusieurs personnes.",
          "Pas de vue de charge consolidée sur tous les projets : les absences se saisissent projet par projet.",
        ],
      },
      {
        note: "Sur les offres payantes, vous pouvez décrire le projet en une phrase (« refonte d'un site vitrine de 10 pages en 8 semaines ») et l'IA propose un premier plan de 5 à 12 tâches, avec durées, dépendances et jalons, que vous ajustez ensuite. L'essai de 14 jours et le plan gratuit n'incluent pas la génération par IA.",
      },
    ],
    faq: [
      {
        q: "Mon client peut-il consulter le planning sans créer de compte ?",
        a: "Oui. Chaque projet peut être partagé par un lien en lecture seule ; le client l'ouvre dans son navigateur sans compte. Vous pouvez protéger le lien par un mot de passe, lui donner une date d'expiration et le supprimer à tout moment.",
      },
      {
        q: "Peut-on mettre le logo de l'agence sur le planning ?",
        a: "Oui, sur les offres Pro et Équipe : le logo importé apparaît sur les exports et sur les pages partagées.",
      },
      {
        q: "Combien coûte FlowGantt pour une agence ?",
        a: PRICING_ANSWER,
      },
      {
        q: "Peut-on importer un planning existant depuis Excel ?",
        a: "Oui. FlowGantt importe un fichier XLSX (colonnes Titre, Début et Fin, plus Description, Progression, Jalon, Assigné et Dépendances en option) et exporte chaque plan en XLSX. Voir [Excel ou FlowGantt pour planifier un projet](/blog/flowgantt-vs-excel).",
      },
    ],
    related: ["dependances-fs-ss-ff-sf-expliquees", "jalons-milestones-comment-les-utiliser", "flowgantt-vs-excel"],
  },

  {
    slug: "freelances",
    label: "Freelances et consultants",
    title: "Planning Gantt pour freelances et consultants — FlowGantt",
    h1: "Un planning Gantt clair pour vendre et tenir vos missions de freelance",
    description:
      "Freelances et consultants : construisez le calendrier de votre mission, joignez-le à votre proposition en PDF et partagez son avancement par lien.",
    updated: "2026-10-30",
    keywords: [
      "planning freelance",
      "logiciel gantt freelance",
      "planning mission consultant",
      "calendrier proposition commerciale",
      "rétroplanning freelance",
    ],
    summary:
      "Pour un freelance ou un consultant, FlowGantt sert à deux moments : avant la signature, pour joindre à la proposition un calendrier crédible exporté en PDF ; pendant la mission, pour partager l'avancement par un lien en lecture seule que le client consulte sans compte. Le plan gratuit suffit pour une mission à la fois (1 projet actif, 30 tâches) ; l'offre Pro à 9 € par mois lève ces limites et ajoute votre logo sur les exports.",
    body: [
      { h2: "Le calendrier, argument de vente" },
      {
        p: "Dans une proposition commerciale, le calendrier rassure autant que le prix : il montre que vous avez découpé la mission, identifié les dépendances et prévu les moments où le client devra intervenir. Un diagramme de Gantt le dit en une page.",
      },
      {
        ul: [
          "**Découpez la mission** en phases et en livrables, chacun avec une durée.",
          "**Placez les jalons** qui engagent le client : atelier de lancement, validation intermédiaire, livraison finale.",
          "**Reliez les tâches** pour que le calendrier se recalcule si la date de démarrage glisse.",
          "**Exportez en PDF ou en PNG** et joignez le planning à la proposition.",
        ],
      },
      { h2: "Construire le calendrier à rebours" },
      {
        p: "Quand le client impose une date de livraison, partez de cette date et remontez : c'est le principe du [rétro-planning](/blog/retro-planning-comment-le-construire). Posez le jalon final, puis chaque tâche avant celle qui en dépend. Si la date de démarrage calculée est déjà passée, vous le voyez avant de signer, pas pendant la mission.",
      },
      { h2: "Pendant la mission" },
      {
        table: {
          head: ["Situation", "Ce que vous faites dans FlowGantt"],
          rows: [
            ["Le client demande où en est le projet", "Vous lui envoyez le lien de partage une fois : il voit toujours la version à jour"],
            ["Un retour client arrive en retard", "Vous déplacez la tâche : les tâches dépendantes se décalent en cascade"],
            ["Vous êtes absent une semaine", "Vous saisissez l'absence : elle apparaît en bande sur le Gantt, pour ajuster les tâches concernées"],
            ["Vous voulez suivre vos tâches au jour le jour", "La vue Kanban montre les mêmes tâches, classées par statut"],
            ["La mission se répète chez un autre client", "Vous enregistrez le projet comme modèle et le réutilisez"],
          ],
        },
      },
      { h2: "Quelle offre choisir quand on est indépendant" },
      {
        p: "Le **plan gratuit** convient à une mission à la fois : 1 projet actif de 30 tâches au maximum, sans carte bancaire et sans limite de durée. Dès que vous menez plusieurs missions en parallèle, ou que vous voulez votre logo sur les exports, l'offre **Pro** (9 € par mois ou 90 € par an) lève les limites et ajoute la génération du premier plan par IA, réservée aux abonnés. L'offre Équipe n'a d'intérêt que si vous travaillez avec d'autres personnes qui doivent modifier les plannings.",
      },
      {
        note: "FlowGantt ne gère ni devis, ni facturation, ni temps passé : il s'utilise à côté de vos outils de gestion, pour le calendrier seulement.",
      },
    ],
    faq: [
      {
        q: "Le plan gratuit suffit-il pour un freelance ?",
        a: "Oui pour une mission à la fois : il permet 1 projet actif de 30 tâches maximum, avec les dépendances, les jalons, le partage par lien et les exports PDF, PNG et XLSX. Il n'inclut ni la génération par IA (réservée aux abonnés Pro et Équipe) ni le logo personnalisé.",
      },
      {
        q: "Puis-je joindre le planning à une proposition commerciale ?",
        a: "Oui : exportez le Gantt en PDF ou en PNG et joignez-le au document. Sur l'offre Pro, l'export porte votre logo.",
      },
      {
        q: "Combien coûte FlowGantt ?",
        a: PRICING_ANSWER,
      },
      {
        q: "Peut-on faire un planning de mission avec ChatGPT plutôt qu'un logiciel ?",
        a: "Un assistant conversationnel peut proposer une liste de tâches, mais il ne recalcule pas les dates quand le projet bouge. Le guide [faire un diagramme de Gantt avec ChatGPT](/blog/diagramme-de-gantt-chatgpt) compare les deux approches.",
      },
    ],
    related: ["retro-planning-comment-le-construire", "modele-planning-projet-gratuit", "diagramme-de-gantt-chatgpt"],
  },

  {
    slug: "btp",
    label: "BTP et chantiers",
    title: "Planning de chantier en diagramme de Gantt — FlowGantt",
    h1: "Un planning de chantier en Gantt qui suit les aléas",
    description:
      "Planifiez un chantier en ligne : lots enchaînés par dépendances, délais d'attente, jours fériés importés et planning partagé avec le maître d'ouvrage.",
    updated: "2026-10-30",
    keywords: [
      "planning de chantier",
      "planning chantier gantt",
      "logiciel planning btp",
      "planning travaux en ligne",
      "planning gros oeuvre second oeuvre",
    ],
    summary:
      "FlowGantt permet de construire un planning de chantier en diagramme de Gantt en ligne : chaque lot ou corps d'état est une tâche, les enchaînements sont des dépendances avec délais (par exemple un temps de séchage entre deux interventions), et, avec l'option calendrier ouvré, les week-ends et les jours fériés importés sont sautés dans le calcul des décalages. Quand un lot prend du retard, les lots suivants se décalent en cascade. Le planning se partage par lien en lecture seule avec le maître d'ouvrage et s'exporte en PDF, PNG ou XLSX.",
    body: [
      { h2: "Pourquoi un Gantt pour un chantier" },
      {
        p: "Un chantier est une suite d'interventions qui dépendent les unes des autres : on ne pose pas le carrelage avant la chape, ni les cloisons avant les réseaux. Le diagramme de Gantt rend ces enchaînements visibles et montre immédiatement l'effet d'un retard sur la date de réception.",
      },
      { h2: "Traduire un chantier dans FlowGantt" },
      {
        table: {
          head: ["Sur le chantier", "Dans FlowGantt"],
          rows: [
            ["Un lot ou un corps d'état (gros œuvre, plomberie, électricité…)", "Une tâche, avec ses sous-tâches si le lot se découpe"],
            ["« Le carreleur intervient après la chape »", "Dépendance Fin → Début (FS)"],
            ["« Le peintre commence quand le plaquiste a avancé »", "Dépendance Début → Début (SS) avec un décalage"],
            ["Un temps de séchage ou de cure", "Un décalage positif sur la dépendance"],
            ["Réception, levée des réserves, passage du bureau de contrôle", "Des jalons"],
            ["Jours fériés et week-ends", "Option calendrier ouvré du projet et import des jours fériés par pays"],
            ["Indisponibilité d'une équipe", "Absence saisie sur le planning"],
          ],
        },
      },
      {
        note: "Par défaut, les décalages des dépendances se comptent en jours calendaires. Avec l'option **calendrier ouvré** du projet, ils se comptent en **jours ouvrés** : week-ends et jours fériés du projet sont sautés. Choisissez l'option selon votre chantier : un séchage continue le week-end, une équipe n'intervient pas le dimanche. Un délai qui ne suit pas la règle du projet peut être porté par une tâche « Séchage » de la durée voulue.",
      },
      { h2: "Exemple : rénovation d'une salle de bains" },
      {
        ol: [
          "**Dépose** de l'existant (2 jours).",
          "**Plomberie et électricité** (3 jours), après la dépose (FS).",
          "**Chape** (1 jour), après les réseaux (FS), puis un décalage de séchage avant le carrelage.",
          "**Carrelage et faïence** (4 jours), après le séchage de la chape.",
          "**Pose des équipements** (2 jours), puis le jalon « Réception ».",
        ],
      },
      {
        p: "Si la plomberie prend un jour de plus, la chape, le carrelage, la pose et la réception se décalent d'un jour, sans rien ressaisir. Pour repérer les tâches qui n'ont aucune marge, lisez le guide du [chemin critique](/blog/chemin-critique-methode-cpm) et celui de la [marge libre et de la marge totale](/blog/marge-libre-marge-totale).",
      },
      { h2: "Partager le planning avec le maître d'ouvrage et les entreprises" },
      {
        p: "Le lien de partage en lecture seule s'ouvre dans un navigateur, sans compte : maître d'ouvrage et entreprises voient toujours la dernière version. Pour une réunion de chantier, exportez le Gantt en PDF ou en PNG. Avec l'offre Équipe, les conducteurs de travaux ont un rôle Manager et les autres membres peuvent être limités à la mise à jour de leur avancement.",
      },
      { h2: "Limites à connaître" },
      {
        ul: [
          "FlowGantt ne gère ni métrés, ni budget, ni approvisionnements : c'est un outil de planification.",
          "Le chemin critique n'est pas calculé automatiquement ; il se lit à partir des dépendances.",
          "Un planning complexe de plusieurs centaines de tâches demande l'offre Pro (le plan gratuit est limité à 30 tâches).",
        ],
      },
    ],
    faq: [
      {
        q: "Peut-on tenir compte des jours fériés dans un planning de chantier ?",
        a: "Oui. Les jours fériés s'importent par pays et par année dans chaque projet ; avec l'option calendrier ouvré, les décalages des dépendances sautent les week-ends et ces jours fériés.",
      },
      {
        q: "Comment représenter un temps de séchage ?",
        a: "Par un décalage positif sur la dépendance entre les deux tâches (par exemple chape → carrelage). Le décalage se compte en jours calendaires, ou en jours ouvrés si le projet utilise le calendrier ouvré ; un délai qui ne suit pas cette règle peut devenir une tâche « Séchage » de la durée voulue.",
      },
      {
        q: "Le maître d'ouvrage peut-il consulter le planning ?",
        a: "Oui, par un lien en lecture seule, sans compte. Le lien peut être protégé par un mot de passe et recevoir une date d'expiration.",
      },
      {
        q: "Combien coûte FlowGantt ?",
        a: PRICING_ANSWER,
      },
    ],
    related: ["chemin-critique-methode-cpm", "dependances-fs-ss-ff-sf-expliquees", "marge-libre-marge-totale"],
  },

  {
    slug: "evenementiel",
    label: "Événementiel",
    title: "Rétro-planning d'événement en diagramme de Gantt — FlowGantt",
    h1: "Le rétro-planning de votre événement, de la date J à aujourd'hui",
    description:
      "Organisez un salon, un séminaire ou un mariage avec un rétro-planning Gantt en ligne : jalons, dépendances et planning partagé avec vos prestataires.",
    updated: "2026-10-30",
    keywords: [
      "rétroplanning événement",
      "planning événementiel",
      "rétro-planning séminaire",
      "planning organisation salon",
      "logiciel planning événementiel",
    ],
    summary:
      "Un événement a une date fixe : son planning se construit à rebours, de la date J vers aujourd'hui. Dans FlowGantt, la date de l'événement et les échéances clés (réservation du lieu, ouverture des inscriptions, bouclage) sont des jalons, les tâches sont reliées par des dépendances, et le planning se partage par lien en lecture seule avec les prestataires. FlowGantt inclut un modèle d'organisation d'événement prêt à adapter.",
    body: [
      { h2: "Pourquoi l'événementiel se planifie à rebours" },
      {
        p: "La date de l'événement ne bouge pas ; ce sont les tâches qui doivent s'y adapter. Le [rétro-planning](/blog/retro-planning-comment-le-construire) part de la date J et remonte : chaque tâche est placée avant celle qui en dépend, avec les délais propres à chaque prestataire. On voit tout de suite la date à laquelle il fallait commencer, et s'il est encore temps.",
      },
      { h2: "Les jalons d'un événement" },
      {
        ul: [
          "**Lieu réservé** : la décision qui conditionne presque toutes les autres.",
          "**Programme arrêté** : intervenants, horaires, ateliers.",
          "**Ouverture des inscriptions** ou envoi des invitations.",
          "**Bouclage** : nombre de participants transmis au traiteur, plan de salle, badges.",
          "**Jour J**, puis le bilan après l'événement.",
        ],
      },
      { h2: "Ce que FlowGantt apporte" },
      {
        table: {
          head: ["Besoin", "Dans FlowGantt"],
          rows: [
            ["Ne pas partir d'une page blanche", "Modèle intégré d'organisation d'événement, à adapter"],
            ["Voir l'effet d'un retard de prestataire", "Dépendances avec décalages : un retard se répercute en cascade"],
            ["Partager le planning avec le lieu, le traiteur, l'agence", "Lien en lecture seule, protégeable par mot de passe et à durée limitée"],
            ["Suivre les tâches au quotidien", "Vue Kanban et vue liste, sur les mêmes données que le Gantt"],
            ["Imprimer le planning pour une réunion", "Exports PDF et PNG ; export XLSX pour le retravailler"],
            ["Travailler à plusieurs", "Offre Équipe : rôles Manager et Membre, invitations par e-mail"],
          ],
        },
      },
      { h2: "Exemple : un séminaire d'entreprise" },
      {
        p: "Pour un séminaire de 80 personnes, un rétro-planning type place la réservation du lieu en premier jalon, puis le programme, les invitations, le bouclage des inscriptions et la commande au traiteur, chacune reliée à la précédente. Si le lieu confirme une semaine plus tard que prévu, toutes les tâches qui en dépendent se décalent : vous voyez aussitôt si le bouclage tient encore avant la date J.",
      },
      {
        note: "Sur les offres Pro et Équipe, décrivez l'événement en une phrase (« séminaire de 80 personnes dans 3 mois ») : l'IA propose un premier plan avec durées, dépendances et jalons, que vous ajustez. Le plan gratuit et l'essai de 14 jours n'incluent pas cette génération, réservée aux abonnés.",
      },
    ],
    faq: [
      {
        q: "Qu'est-ce qu'un rétro-planning d'événement ?",
        a: "C'est un planning construit à rebours à partir de la date de l'événement : on place d'abord le jour J, puis chaque tâche avant celle qui en dépend. Le guide [rétro-planning : comment le construire](/blog/retro-planning-comment-le-construire) détaille la méthode.",
      },
      {
        q: "FlowGantt convient-il pour organiser un mariage ?",
        a: "Oui : un mariage se planifie comme tout événement, avec des jalons (lieu, traiteur, faire-part) et des dépendances. Le plan gratuit (1 projet actif, 30 tâches) suffit souvent.",
      },
      {
        q: "Les prestataires peuvent-ils voir le planning ?",
        a: "Oui, par un lien de partage en lecture seule qui s'ouvre sans compte, avec mot de passe et date d'expiration en option.",
      },
      {
        q: "Combien coûte FlowGantt ?",
        a: PRICING_ANSWER,
      },
    ],
    related: ["retro-planning-comment-le-construire", "jalons-milestones-comment-les-utiliser", "modele-planning-projet-gratuit"],
  },
];

export default useCases;
